import type React from "react"
import { useNavigate } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import Content from "components/Content"
import Card from "components/Card"
import EmptyState from "components/EmptyState"
import Loading from "components/Loading"
import { Button, Group, Text, UnstyledButton, ActionIcon } from "@mantine/core"
import {
  useNotificationsList,
  useMarkAsRead,
  useMarkAllAsRead,
} from "../hooks/useNotifications"
import type { Notification } from "../models/notification"
import { buildDrawerNavigation } from "../../../helpers/drawerQueryParams"

const getNotificationIconInfo = (className: string) => {
  const normalizedClass = className.toLowerCase()
  if (normalizedClass.includes("document")) {
    return { icon: "fa fa-file-text-o", colorClass: "text-blue-500", bgClass: "bg-blue-50" }
  }
  if (normalizedClass.includes("agencyvisit")) {
    return { icon: "fa fa-map-marker", colorClass: "text-green-500", bgClass: "bg-green-50" }
  }
  if (normalizedClass.includes("travelercheck")) {
    return { icon: "fa fa-ticket", colorClass: "text-teal-500", bgClass: "bg-teal-50" }
  }
  if (normalizedClass.includes("post")) {
    return { icon: "fa fa-comments-o", colorClass: "text-orange-500", bgClass: "bg-orange-50" }
  }
  return { icon: "fa fa-bell-o", colorClass: "text-gray-500", bgClass: "bg-gray-50" }
}

export default function NotificationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Fetch 100 notifications for full list view
  const { data: listData, isLoading } = useNotificationsList(100)
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()

  const hasUnread = listData?.some((n) => !n.read) ?? false

  const handleItemClick = (notif: Notification) => {
    if (!notif.read) {
      markAsRead.mutate(notif.id)
    }
    const drawerNav = buildDrawerNavigation(notif.url)
    if (drawerNav) {
      navigate(drawerNav)
    } else if (notif.url?.startsWith("http")) {
      window.open(notif.url, "_blank", "noopener,noreferrer")
    } else if (notif.url) {
      navigate(notif.url)
    }
  }

  const handleMarkAsReadClick = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    markAsRead.mutate(id)
  }

  if (isLoading) {
    return (
      <Content title={<Trans i18nKey="notifications.title" />}>
        <div className="flex justify-center items-center py-20">
          <Loading />
        </div>
      </Content>
    )
  }

  return (
    <Content title={<Trans i18nKey="notifications.title" />}>
      <div className="flex justify-end mb-4">
        {hasUnread && (
          <Button
            onClick={() => markAllAsRead.mutate()}
            loading={markAllAsRead.isPending}
            variant="light"
            color="blue"
            leftSection={<i className="fa fa-check-square-o" />}
          >
            <Trans i18nKey="notifications.markAllAsRead" />
          </Button>
        )}
      </div>

      <Card title={<Trans i18nKey="notifications.title" />}>
        {!listData || listData.length === 0 ? (
          <EmptyState padding="lg">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <i className="fa fa-bell-o text-gray-300 text-5xl mb-4" />
              <Text className="text-gray-500 text-lg">
                <Trans i18nKey="notifications.empty" />
              </Text>
            </div>
          </EmptyState>
        ) : (
          <div className="divide-y divide-gray-200">
            {listData.map((notif) => {
              const iconInfo = getNotificationIconInfo(notif.class)
              return (
                <UnstyledButton
                  key={notif.id}
                  onClick={() => handleItemClick(notif)}
                  className={`flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer w-full ${
                    !notif.read ? "bg-blue-50/10" : ""
                  }`}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {/* Circle icon container */}
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${iconInfo.bgClass}`}
                    >
                      <i className={`${iconInfo.icon} ${iconInfo.colorClass} text-lg`} />
                    </div>

                    {/* Message content */}
                    <div className="min-w-0 flex-1">
                      <Text
                        size="sm"
                        className={`text-gray-800 break-words ${!notif.read ? "font-semibold text-gray-950" : ""}`}
                      >
                        {notif.message}
                      </Text>
                      <Text size="xs" className="text-gray-400 mt-1">
                        {notif.timeago}
                      </Text>
                    </div>
                  </div>

                  {/* Mark read/checkmark status */}
                  <div className="ml-4 flex-shrink-0">
                    {!notif.read ? (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          leftSection={<i className="fa fa-check" />}
                          onClick={(e) => handleMarkAsReadClick(e, notif.id)}
                          loading={markAsRead.isPending && markAsRead.variables === notif.id}
                        >
                          <Trans i18nKey="notifications.markAsRead" />
                        </Button>
                      </Group>
                    ) : (
                      <Text size="xs" className="text-gray-400 flex items-center gap-1 font-medium">
                        <i className="fa fa-check text-green-500" /> <Trans i18nKey="notifications.read" />
                      </Text>
                    )}
                  </div>
                </UnstyledButton>
              )
            })}
          </div>
        )}
      </Card>
    </Content>
  )
}
